import React, { useState, useEffect } from 'react';
import { PlusCircle, CheckCircle, Circle, Trash2, ArrowLeft, Menu } from 'lucide-react';
import './DopamineTracker.css';

// Main App Component
const DopamineTracker = () => {
  // Load goals and points from localStorage or use defaults
  const [goals, setGoals] = useState(() => {
    const savedGoals = localStorage.getItem('dopamineTrackerGoals');
    return savedGoals ? JSON.parse(savedGoals) : [];
  });
  
  const [points, setPoints] = useState(() => {
    const savedPoints = localStorage.getItem('dopamineTrackerPoints');
    return savedPoints ? parseInt(savedPoints) : 0;
  });
  
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newSubgoalTitle, setNewSubgoalTitle] = useState('');
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // Save goals and points to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('dopamineTrackerGoals', JSON.stringify(goals));
  }, [goals]);
  
  useEffect(() => {
    localStorage.setItem('dopamineTrackerPoints', points.toString());
  }, [points]);

  // Toggle mobile detail view when a goal is selected
  useEffect(() => {
    if (selectedGoalId) {
      setShowMobileDetail(true);
    }
  }, [selectedGoalId]);

  const selectedGoal = goals.find(goal => goal.id === selectedGoalId);

  // Add a new goal
  const handleAddGoal = () => {
    if (newGoalTitle.trim() === '') return;
    
    const newGoal = {
      id: Date.now().toString(),
      title: newGoalTitle.trim(),
      subgoals: [],
      createdAt: new Date().toISOString()
    };
    
    setGoals([...goals, newGoal]);
    setNewGoalTitle('');
    setSelectedGoalId(newGoal.id);
  };

  // Add a subgoal to the selected goal
  const handleAddSubgoal = () => {
    if (newSubgoalTitle.trim() === '' || !selectedGoalId) return;
    
    const newSubgoal = {
      id: Date.now().toString(),
      title: newSubgoalTitle.trim(),
      completed: false,
      createdAt: new Date().toISOString()
    };
    
    setGoals(goals.map(goal => {
      if (goal.id === selectedGoalId) {
        return {
          ...goal,
          subgoals: [...goal.subgoals, newSubgoal]
        };
      }
      return goal;
    }));
    
    setNewSubgoalTitle('');
  };

  // Toggle completion status of a subgoal and update points
  const toggleSubgoalCompletion = (subgoalId) => {
    // Find the current subgoal to check its current state
    const currentGoal = goals.find(goal => goal.id === selectedGoalId);
    const currentSubgoal = currentGoal?.subgoals.find(sg => sg.id === subgoalId);
    const currentlyCompleted = currentSubgoal?.completed || false;
    
    // Update points based on the toggle action
    if (!currentlyCompleted) {
      // Add points when marking as complete
      setPoints(prevPoints => prevPoints + 10);
    } else {
      // Remove points when unmarking as complete
      setPoints(prevPoints => Math.max(0, prevPoints - 10));
    }
    
    // Update the goals state
    setGoals(goals.map(goal => {
      if (goal.id === selectedGoalId) {
        return {
          ...goal,
          subgoals: goal.subgoals.map(subgoal => {
            if (subgoal.id === subgoalId) {
              return {
                ...subgoal,
                completed: !subgoal.completed
              };
            }
            return subgoal;
          })
        };
      }
      return goal;
    }));
  };

  // Delete a goal
  const handleDeleteGoal = (goalId, e) => {
    e.stopPropagation();
    setGoals(goals.filter(goal => goal.id !== goalId));
    if (selectedGoalId === goalId) {
      setSelectedGoalId(null);
    }
  };

  // Calculate completion percentage for a goal
  const calculateCompletion = (goal) => {
    if (goal.subgoals.length === 0) return 0;
    const completedCount = goal.subgoals.filter(sg => sg.completed).length;
    return Math.round((completedCount / goal.subgoals.length) * 100);
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        <div>
          <h1 className="app-title">Dopamine Tracker</h1>
          <p className="app-subtitle">Track your goals and get that satisfaction hit</p>
        </div>
        <div className="points-display">
          <span className="points-value">{points}</span>
          <span className="points-label">points</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content container">
        {/* Goals List */}
        <div className={`goals-panel ${showMobileDetail ? 'mobile-hidden' : ''}`}>
          <h2 className="panel-title">
            <span className="title-accent"></span>
            Your Goals
          </h2>
          
          {/* New Goal Input */}
          <div className="goal-input-container">
            <input
              type="text"
              className="goal-input"
              placeholder="Add a new goal..."
              value={newGoalTitle}
              onChange={(e) => setNewGoalTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddGoal()}
            />
            <button 
              className="add-goal-btn"
              onClick={handleAddGoal}
            >
              <PlusCircle size={20} />
            </button>
          </div>
          
          {/* Goals List */}
          <div className="goals-list">
            {goals.length === 0 ? (
              <div className="empty-goals-message">
                <div className="empty-icon">
                  <PlusCircle size={36} className="mx-auto" />
                </div>
                <p>No goals yet. Create your first goal!</p>
              </div>
            ) : (
              goals.map(goal => (
                <div 
                  key={goal.id}
                  className={`goal-item ${selectedGoalId === goal.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGoalId(goal.id)}
                >
                  <div className="goal-info">
                    <h3 className="goal-name">{goal.title}</h3>
                    <div className="progress-container">
                      <div className="progress-bar-bg">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${calculateCompletion(goal)}%` }}
                        ></div>
                      </div>
                      <span className="progress-value">{calculateCompletion(goal)}%</span>
                    </div>
                  </div>
                  <button 
                    className="delete-btn"
                    onClick={(e) => handleDeleteGoal(goal.id, e)}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Selected Goal Details */}
        <div className={`details-panel ${!showMobileDetail ? 'mobile-hidden' : ''}`}>
          {selectedGoal ? (
            <>
              <div className="goal-header">
                <button 
                  className="back-to-goals-btn"
                  onClick={() => setShowMobileDetail(false)}
                >
                  <ArrowLeft size={18} />
                  <span>Back to Goals</span>
                </button>
                <h2 className="goal-title">{selectedGoal.title}</h2>
                <div className="goal-progress">
                  <div className="goal-progress-bar-bg">
                    <div 
                      className="goal-progress-bar" 
                      style={{ width: `${calculateCompletion(selectedGoal)}%` }}
                    ></div>
                  </div>
                  <span className="goal-progress-value">{calculateCompletion(selectedGoal)}% Complete</span>
                </div>
              </div>
              
              {/* Add Subgoal Form */}
              <div className="milestone-form">
                <h3 className="milestone-form-title">
                  <PlusCircle size={18} className="milestone-icon" />
                  Add a Milestone
                </h3>
                <div className="milestone-form-inputs">
                  <input
                    type="text"
                    className="milestone-input"
                    placeholder="What's your next milestone?"
                    value={newSubgoalTitle}
                    onChange={(e) => setNewSubgoalTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSubgoal()}
                  />
                  <button 
                    className="add-milestone-btn"
                    onClick={handleAddSubgoal}
                  >
                    Add Milestone
                  </button>
                </div>
              </div>
              
              {/* Timeline Visualization */}
              <div className="timeline-section">
                <h3 className="timeline-title">
                  <span className="timeline-accent"></span>
                  Goal Timeline
                </h3>
                {selectedGoal.subgoals.length === 0 ? (
                  <div className="empty-timeline-message">
                    <p>No milestones yet. Add your first milestone above!</p>
                  </div>
                ) : (
                  <div className="timeline-container">
                    {/* Timeline Line */}
                    <div className="timeline-line"></div>
                    
                    {/* Timeline Horizontal Bar */}
                    <div className="timeline-items">
                      {selectedGoal.subgoals.map((subgoal, index) => (
                        <div 
                          key={subgoal.id}
                          className={`timeline-item ${
                            index === 0 ? 'timeline-item-first' : subgoal.completed ? 
                            'timeline-item-completed' : 'timeline-item-pending'
                          }`}
                        >
                          <button 
                            className="milestone-btn"
                            onClick={() => toggleSubgoalCompletion(subgoal.id)}
                          >
                            {subgoal.completed ? (
                              <div className="milestone-check-container">
                                <CheckCircle className="milestone-check" size={36} />
                                <div className="milestone-ping"></div>
                              </div>
                            ) : (
                              <Circle className="milestone-circle" size={36} />
                            )}
                            <span className={`milestone-label ${
                              subgoal.completed ? 
                              'milestone-label-completed' : 'milestone-label-pending'
                            }`}>
                              {subgoal.title}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <CheckCircle size={48} />
              </div>
              <p className="empty-state-title">Select a goal or create a new one to get started</p>
              <p className="empty-state-desc">Track your progress, celebrate your achievements, and get that dopamine hit with every completed milestone!</p>
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="footer">
        <p>Dopamine Tracker - Get satisfaction with every completed milestone</p>
      </footer>
    </div>
  );
};

export default DopamineTracker;
